import { api } from '../../AxiosConfig';

export type WhatsappConversation = {
    _id: string;
    estabId: string;
    userId?: string;
    clientPhoneNumber: string;
    profileName?: string;
    clientName?: string;
    lastMessageAt?: string;
    lastInboundAt?: string;
    lastOutboundAt?: string;
    lastMessagePreview?: string;
    lastMessageDirection?: 'INBOUND' | 'OUTBOUND';
    unreadCount?: number;
    botPausedUntil?: string;
    botPaused?: boolean;
    customerServiceWindowOpen?: boolean;
};

export type WhatsappMessage = {
    _id: string;
    estabId: string;
    conversationId: string;
    clientPhoneNumber: string;
    direction: 'INBOUND' | 'OUTBOUND';
    senderType: 'CLIENT' | 'BOT' | 'ATTENDANT' | 'SYSTEM';
    messageType: 'text' | 'interactive' | 'template' | 'button' | 'image' | 'video' | 'audio' | 'unknown';
    body?: string;
    templateName?: string;
    status: 'received' | 'sent' | 'delivered' | 'read' | 'failed';
    errorMessage?: string;
    deliveredAt?: string;
    readAt?: string;
    failedAt?: string;
    statusUpdatedAt?: string;
    createdAt: string;
    createdBy?: string;
    metadata?: any;
};

export type WhatsappManualTemplate = {
    name: string;
    label: string;
    languageCode: string;
    description?: string;
};

export type WhatsappStartConversationPayload = {
    clientId?: string;
    name?: string;
    phoneNumber?: string;
};

export type WhatsappAudioPayload = {
    fileContent: string;
    fileName: string;
    fileType: string;
    size: number;
};

export type WhatsappClientOverview = {
    conversation: WhatsappConversation;
    client: {
        linked: boolean;
        uid?: string;
        displayName?: string;
        email?: string;
        phoneNumber?: string;
        photoURL?: string;
        gruposIds?: string[];
        atendentes?: string[];
        selfSchedulingBlocked?: boolean;
        selfSchedulingBlockMode?: 'ALL' | 'WEEK_DAYS' | null;
        selfSchedulingBlockedWeekDays?: number[];
    };
    metrics: {
        totalAppointments: number;
        completedAppointments: number;
        upcomingAppointments: number;
        canceledAppointments: number;
        totalSpent: number;
        activeSubscriptions: number;
    };
    appointments: Array<{
        _id: string;
        startAt?: string;
        endAt?: string;
        status?: string;
        serviceName?: string;
        extraServices?: string[];
        totalPrice?: number;
        attendantId?: string;
        attendantName?: string;
        assinaturaId?: string;
    }>;
    subscriptions: Array<{
        _id: string;
        status?: number;
        billingType?: string;
        planName?: string;
        price?: number;
        startAt?: string;
        endAt?: string;
        nextBillingAt?: string;
        paidPeriodsCount?: number;
        remainingPeriodsCount?: number;
        totalPaidAmount?: number;
        totalRemainingAmount?: number;
        lastPaymentAt?: string;
        lastBillingStatus?: string;
    }>;
};

class WhatsappInboxService {
    findConversations(search?: string) {
    return api.get<WhatsappConversation[]>('/whatsapp/conversations', {
            params: search ? { search } : undefined,
    });
  }

    deleteConversation(conversationId: string) {
        return api.delete<{ deleted: true; conversationId: string }>(`/whatsapp/conversations/${conversationId}`);
    }

    findMessages(conversationId: string) {
        return api.get<{
            conversation: WhatsappConversation;
            messages: WhatsappMessage[];
        }>(`/whatsapp/conversations/${conversationId}/messages`);
    }

    findMessageMedia(conversationId: string, messageId: string) {
        return api.get<Blob>(`/whatsapp/conversations/${conversationId}/messages/${messageId}/media`, {
            responseType: 'blob',
        });
    }

    findClientOverview(conversationId: string) {
        return api.get<WhatsappClientOverview>(`/whatsapp/conversations/${conversationId}/client-overview`);
    }

    findManualTemplates() {
        return api.get<WhatsappManualTemplate[]>('/whatsapp/manual-templates');
    }

    startConversation(payload: WhatsappStartConversationPayload) {
        return api.post<WhatsappConversation>('/whatsapp/conversations/start', payload);
    }

    sendMessage(conversationId: string, message: string, templateName?: string) {
        return api.post<{
            conversation: WhatsappConversation;
            message: WhatsappMessage;
            deliveryMode: 'text' | 'template';
            templateName?: string;
        }>(`/whatsapp/conversations/${conversationId}/send`, { message, templateName });
    }

    sendAudio(conversationId: string, payload: WhatsappAudioPayload) {
        return api.post<{
            conversation: WhatsappConversation;
            message: WhatsappMessage;
            deliveryMode: 'audio';
        }>(`/whatsapp/conversations/${conversationId}/send-audio`, payload);
    }

    resumeBot(conversationId: string) {
        return api.post<WhatsappConversation>(`/whatsapp/conversations/${conversationId}/resume-bot`);
    }

    markAsRead(conversationId: string) {
        return api.post<WhatsappConversation>(`/whatsapp/conversations/${conversationId}/read`);
    }
}

export default WhatsappInboxService;
